import type { ReactNode } from "react";
import { useScrollReveal } from "@/hooks/useScrollReveal";

interface MissionCardProps {
  title: string;
  subtitle?: string;
  status: "urgent" | "active" | "pending" | "completed" | "blocked";
  icon: string;
  color: "primary" | "accent" | "secondary" | "purple" | "red" | "emerald" | "amber" | "sky";
  progress?: number; // 0-100
  deadline?: string;
  daysRemaining?: number;
  onClick?: () => void;
  children?: ReactNode;
  badge?: string;
  className?: string;
  delay?: number;
  animate?: boolean;
}

const COLOR_MAP: Record<string, { bg: string; border: string; text: string; iconBg: string; bar: string; glow: string }> = {
  primary: { bg: "bg-primary-50/60", border: "border-primary-200/60", text: "text-primary-700", iconBg: "bg-primary-100", bar: "bg-primary-400", glow: "shadow-glow-primary" },
  accent: { bg: "bg-accent-50/60", border: "border-accent-200/60", text: "text-accent-700", iconBg: "bg-accent-100", bar: "bg-accent-400", glow: "shadow-glow-accent" },
  secondary: { bg: "bg-secondary-50/60", border: "border-secondary-200/60", text: "text-secondary-700", iconBg: "bg-secondary-100", bar: "bg-secondary-400", glow: "shadow-glow-amber" },
  purple: { bg: "bg-purple-50/60", border: "border-purple-200/60", text: "text-purple-700", iconBg: "bg-purple-100", bar: "bg-purple-400", glow: "shadow-glow-purple" },
  red: { bg: "bg-red-50/60", border: "border-red-200/60", text: "text-red-700", iconBg: "bg-red-100", bar: "bg-red-400", glow: "shadow-glow-red" },
  emerald: { bg: "bg-emerald-50/60", border: "border-emerald-200/60", text: "text-emerald-700", iconBg: "bg-emerald-100", bar: "bg-emerald-400", glow: "shadow-glow-emerald" },
  amber: { bg: "bg-amber-50/60", border: "border-amber-200/60", text: "text-amber-700", iconBg: "bg-amber-100", bar: "bg-amber-400", glow: "shadow-glow-amber" },
  sky: { bg: "bg-sky-50/60", border: "border-sky-200/60", text: "text-sky-700", iconBg: "bg-sky-100", bar: "bg-sky-400", glow: "" },
};

const STATUS_ACCENT: Record<string, string> = {
  urgent: "border-l-[4px] border-l-red-500",
  active: "border-l-[4px] border-l-primary-500",
  pending: "border-l-[4px] border-l-amber-400",
  completed: "border-l-[4px] border-l-emerald-500",
  blocked: "border-l-[4px] border-l-foreground-300",
};

export default function MissionCard({
  title, subtitle, status, icon, color, progress, deadline, daysRemaining, onClick, children, badge, className = "", delay = 0, animate = true,
}: MissionCardProps) {
  const c = COLOR_MAP[color] || COLOR_MAP.primary;
  const sa = STATUS_ACCENT[status] || "";

  const cardContent = (
    <div
      onClick={onClick}
      className={`relative overflow-hidden rounded-2xl border ${c.border} ${c.bg} ${sa} p-4 transition-all duration-300 hover-lift cursor-pointer group ${c.glow} ${className} ${animate ? "card-reveal" : ""}`}
      style={delay > 0 ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {/* Top row */}
      <div className="flex items-start gap-3 mb-3">
        <span className={`flex items-center justify-center w-10 h-10 rounded-xl ${c.iconBg} ${c.text} flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
          <i className={`${icon} text-lg`} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-bold text-foreground-900 truncate">{title}</p>
            {badge && (
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold whitespace-nowrap flex-shrink-0 ${
                status === "urgent" ? "bg-red-100 text-red-700" :
                status === "active" ? "bg-primary-100 text-primary-700" :
                status === "completed" ? "bg-emerald-100 text-emerald-700" :
                "bg-amber-100 text-amber-700"
              }`}>
                {badge}
              </span>
            )}
          </div>
          {subtitle && <p className="text-xs text-foreground-500 mt-0.5 truncate">{subtitle}</p>}
        </div>
        {status === "urgent" && (
          <span className="flex-shrink-0 flex items-center justify-center w-3 h-3 rounded-full bg-red-500 animate-pulse shadow-glow-red" />
        )}
        {status === "completed" && (
          <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100">
            <i className="ri-check-line text-emerald-500 text-xs" />
          </span>
        )}
      </div>

      {/* Progress bar */}
      {progress !== undefined && (
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] text-foreground-400 font-medium">Progreso</span>
            <span className="text-[10px] font-bold text-foreground-700">{Math.round(progress)}%</span>
          </div>
          <div className="w-full h-1.5 bg-background-200/70 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-700 ease-out-expo ${c.bar} ${status === "urgent" ? "animate-pulse" : ""}`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Deadline info */}
      {(deadline || daysRemaining !== undefined) && (
        <div className="flex items-center gap-2 mb-2">
          <span className="flex items-center justify-center w-4 h-4">
            <i className={`ri-calendar-line text-xs ${daysRemaining !== undefined && daysRemaining <= 0 ? "text-red-500" : daysRemaining !== undefined && daysRemaining <= 3 ? "text-amber-500" : "text-foreground-400"}`} />
          </span>
          {deadline && <span className="text-xs text-foreground-500">{deadline}</span>}
          {daysRemaining !== undefined && (
            <span className={`text-xs font-bold ml-auto ${
              daysRemaining <= 0 ? "text-red-600" : daysRemaining <= 3 ? "text-amber-600" : "text-foreground-600"
            }`}>
              {daysRemaining <= 0 ? `Vencido (${Math.abs(daysRemaining)}d)` : `${daysRemaining}d restantes`}
            </span>
          )}
        </div>
      )}

      {/* Children / details */}
      {children && <div className="text-xs text-foreground-600">{children}</div>}

      {/* Action hint */}
      <div className="flex items-center gap-1 mt-3 pt-2 border-t border-background-200/50">
        <span className="text-[11px] font-medium text-foreground-500">
          {status === "urgent" ? "Resolver ahora" : status === "active" ? "Continuar gestión" : status === "pending" ? "Iniciar" : status === "completed" ? "Ver detalle" : "Desbloquear"}
        </span>
        <span className="flex items-center justify-center w-3 h-3 text-foreground-400 group-hover:translate-x-0.5 transition-transform">
          <i className="ri-arrow-right-s-line text-xs" />
        </span>
      </div>
    </div>
  );

  if (animate) {
    return cardContent;
  }

  return cardContent;
}